import { Form, Formik, useField } from "formik";
import * as Yup from "yup";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { Client } from "@/features/clients/types";
import {
  FILE_AS_OPTIONS,
  type FileAs,
  type Form2159Data,
  type FormSubmission,
  PAYROLL_FREQUENCY_OPTIONS,
  type PayrollFrequency,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox } from "./formik-controls";

const money = Yup.string().test(
  "non-negative",
  "Must be zero or greater",
  (v) => v == null || v === "" || (!Number.isNaN(Number(v)) && Number(v) >= 0),
);

const schema = Yup.object({
  for_who: Yup.string().oneOf(["T", "S", "J"]).required(),
  total_amount: money,
  tax_pay_per_month: money,
  increase_amt_1: money,
  increase_amt_2: money,
  employer_name: Yup.string().max(150, "Too long"),
  contact_phone: Yup.string().max(255, "Too long"),
});

function toInitialValues(data: Record<string, unknown>): Form2159Data {
  const d = data as Partial<Form2159Data>;
  return {
    for_who: d.for_who ?? "T",
    tax_form_no: d.tax_form_no ?? "1040",
    tax_periods: d.tax_periods ?? "",
    total_amount: d.total_amount ?? "0",
    amt_owed_date: d.amt_owed_date ?? "",
    tax_pay_per_month: d.tax_pay_per_month ?? "0",
    first_pmt_date: d.first_pmt_date ?? "",
    no_electronic: d.no_electronic ?? false,
    employer_name: d.employer_name ?? "",
    employer_address: d.employer_address ?? "",
    contact_name: d.contact_name ?? "",
    contact_phone: d.contact_phone ?? "",
    ded_frequency: d.ded_frequency ?? "",
    ded_other: d.ded_other ?? "",
    paid_frequency: d.paid_frequency ?? "",
    paid_other: d.paid_other ?? "",
    sent_by_date: d.sent_by_date ?? "",
    begin_on_date: d.begin_on_date ?? "",
    emp_title: d.emp_title ?? "",
    emp_date: d.emp_date ?? "",
    write_add: d.write_add ?? "",
    deduc_until: d.deduc_until ?? "",
    title_corp_part: d.title_corp_part ?? "",
    increase_amt_1: d.increase_amt_1 ?? "0",
    increase_date_1: d.increase_date_1 ?? "",
    increase_amt_2: d.increase_amt_2 ?? "0",
    increase_date_2: d.increase_date_2 ?? "",
  };
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

function LabeledCell({
  name,
  label,
  ...props
}: { name: string; label: string } & React.ComponentProps<typeof FormikCell>) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={name} className="text-sm font-normal">
        {label}
      </Label>
      <FormikCell name={name} {...props} />
    </div>
  );
}

function ForWhoField({ married }: { married: boolean }) {
  const [field, , helpers] = useField<FileAs>("for_who");
  if (!married) return <p className="text-muted-foreground text-sm">Taxpayer</p>;
  // 2159 is taxpayer- or spouse-only (no joint filing on a single employer).
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as FileAs)}
    >
      {FILE_AS_OPTIONS.filter((o) => o.value !== "J").map((o) => (
        <Label key={o.value} htmlFor={`fw-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`fw-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/** Weekly / Two-week / Monthly / Other frequency radio, revealing a specify box. */
function FrequencyField({
  name,
  otherName,
  label,
}: {
  name: string;
  otherName: string;
  label: string;
}) {
  const [field, , helpers] = useField<PayrollFrequency>(name);
  return (
    <div className="grid gap-2">
      <Label className="text-sm font-normal">{label}</Label>
      <RadioGroup
        className="flex flex-wrap gap-6"
        aria-label={label}
        value={field.value || ""}
        onValueChange={(v) => helpers.setValue(v as PayrollFrequency)}
      >
        {PAYROLL_FREQUENCY_OPTIONS.map((o) => (
          <Label key={o.value} htmlFor={`${name}-${o.value}`} className="flex items-center gap-2 font-normal">
            <RadioGroupItem id={`${name}-${o.value}`} value={o.value} />
            {o.label}
          </Label>
        ))}
      </RadioGroup>
      {field.value === "O" && (
        <FormikCell name={otherName} placeholder="Specify frequency" maxLength={255} />
      )}
    </div>
  );
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form 2159 (Payroll Deduction Agreement) and its Spanish variant. The
 * taxpayer identity + tax periods prefill from the client + transcripts; the
 * employer agreement block, deduction/paid frequency, and scheduled increases
 * are entered here. All three template copies fill from the same data.
 */
export function Form2159Editor({ client, submission, onSubmissionChange }: Props) {
  const married = client.maritalStatus === "MARRIED";
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik initialValues={toInitialValues(submission.data)} validationSchema={schema} onSubmit={() => {}}>
      <Form className="flex flex-col gap-6">
        <Card className="gap-4 p-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-foreground font-semibold">Generate for</span>
            <ForWhoField married={married} />
          </div>
          <div className="text-muted-foreground text-sm">
            <p className="text-foreground font-medium">{client.name}</p>
            {address && <p>{address}</p>}
          </div>
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Employer</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="employer_name" label="Employer name" maxLength={150} />
            <LabeledCell name="employer_address" label="Employer address" maxLength={255} />
            <LabeledCell name="contact_name" label="Contact person" maxLength={150} />
            <LabeledCell name="contact_phone" label="Contact telephone" inputMode="tel" maxLength={255} />
          </div>
          <FrequencyField
            name="ded_frequency"
            otherName="ded_other"
            label="Agree to deduct from wages every"
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="sent_by_date" label="Payment sent by" type="date" />
            <LabeledCell name="begin_on_date" label="Beginning on" type="date" />
            <LabeledCell name="emp_title" label="Employer signature title" maxLength={150} />
            <LabeledCell name="emp_date" label="Employer date" type="date" />
          </div>
          <FormikCheckbox name="no_electronic" label="Unable to make debit payments" />
          <LabeledCell name="write_add" label="…or write to (mailing address)" maxLength={255} />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Amounts &amp; deduction terms</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="tax_form_no" label="Kinds of taxes" maxLength={150} />
            <LabeledCell name="tax_periods" label="Tax periods" maxLength={255} />
            <LabeledCell name="amt_owed_date" label="Amount owed as of" type="date" />
            <LabeledCell name="total_amount" label="Amount owed ($, plus penalties/interest)" inputMode="decimal" />
          </div>
          <FrequencyField name="paid_frequency" otherName="paid_other" label="I am paid every" />
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="tax_pay_per_month" label="Amount to deduct each pay ($)" inputMode="decimal" />
            <LabeledCell name="first_pmt_date" label="Deduction begins" type="date" />
          </div>
          <LabeledCell name="deduc_until" label="Deduct until (e.g. total liability paid in full)" maxLength={25} />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Scheduled increases</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="increase_date_1" label="Increase #1 date" type="date" />
            <LabeledCell name="increase_amt_1" label="Increase #1 amount ($)" inputMode="decimal" />
            <LabeledCell name="increase_date_2" label="Increase #2 date" type="date" />
            <LabeledCell name="increase_amt_2" label="Increase #2 amount ($)" inputMode="decimal" />
          </div>
          <LabeledCell name="title_corp_part" label="Title (if corporate officer or partner)" maxLength={255} />
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
